/**
 * The Normal | High choice and the small High badge (2026-10-31).
 *
 * One choice for the two editors that write a task -- the New task form
 * and the schedule Edit drawer -- so the words, the lock and the help note
 * cannot drift apart. The badge marks a High job wherever a job is listed;
 * a Normal job shows nothing.
 */
import { HelpNote } from '../../help';
import { PRIORITY_HIGH, PRIORITY_NORMAL, type PriorityLevel } from '../../api/task-priority';
import {
  HIGH_PRIORITY_KEPT,
  HIGH_PRIORITY_LOCKED,
  PRIORITY_LABEL,
  priorityEffectText,
} from './task-priority';
import './TaskPriority.css';

export function PriorityChoice({
  value,
  onChange,
  canSetHigh,
  storedHigh = false,
  effects,
  error,
  testId,
}: {
  value: PriorityLevel;
  onChange: (level: PriorityLevel) => void;
  /** The person may choose High (`useCanSetHighPriority`). */
  canSetHigh: boolean;
  /** The task being edited is stored High. Keeping High is not making it
   *  (`task_priority.py`), so High stays choosable for anyone editing it. */
  storedHigh?: boolean;
  /** The site's published effects (`priorityEffects`): null when none,
   *  undefined while not known (no line is said then). */
  effects: Readonly<Record<string, string>> | null | undefined;
  /** The api-server's refusal, in its words. */
  error?: string | null;
  testId: string;
}) {
  const highLocked = !canSetHigh && !storedHigh;
  const effect = priorityEffectText(effects, value);
  const option = (level: PriorityLevel, disabled: boolean) => (
    <button
      type="button"
      className={`nx-prio-choice__opt${value === level ? ' is-active' : ''}`}
      aria-pressed={value === level}
      disabled={disabled}
      title={disabled ? HIGH_PRIORITY_LOCKED : undefined}
      onClick={() => onChange(level)}
      data-testid={`${testId}-${level === PRIORITY_HIGH ? 'high' : 'normal'}`}
    >
      {PRIORITY_LABEL[level]}
    </button>
  );
  return (
    <div className="nx-task-form__row">
      <span>Priority</span>
      <div className="nx-prio-choice" role="group" aria-label="Priority" data-testid={testId}>
        {option(PRIORITY_NORMAL, false)}
        {option(PRIORITY_HIGH, highLocked)}
      </div>
      {effect && (
        <span className="nx-task-form__hint" data-testid={`${testId}-effect`}>
          {effect}
        </span>
      )}
      {highLocked && (
        <span className="nx-task-form__hint" data-testid={`${testId}-locked`}>
          {HIGH_PRIORITY_LOCKED}
        </span>
      )}
      {!canSetHigh && storedHigh && (
        <span className="nx-task-form__hint" data-testid={`${testId}-kept`}>
          {HIGH_PRIORITY_KEPT}
        </span>
      )}
      {error && (
        <span className="nx-task-form__field-error" role="alert" data-testid={`${testId}-error`}>
          {error}
        </span>
      )}
      <HelpNote id="tasks.priority" />
    </div>
  );
}

/** "High", on a job marked High. Read by a screen reader as "High
 *  priority"; `compact` drops the padding for a lane bar, `lead` spaces
 *  it from the words it comes before. */
export function PriorityBadge({
  compact = false,
  lead = false,
  testId,
}: {
  compact?: boolean;
  lead?: boolean;
  testId?: string;
}) {
  return (
    <span
      className={`nx-prio-badge${compact ? ' is-compact' : ''}${lead ? ' is-lead' : ''}`}
      title="High priority"
      data-testid={testId}
    >
      High<span className="nx-sr-only"> priority</span>
    </span>
  );
}
